import React, { useEffect } from 'react';
import { Outlet, useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Landmark, Vote, Users, FileBarChart, Package, Map, LayoutDashboard, LogOut, Languages, MapPinned, FileText, Download, Upload, Crown } from 'lucide-react';

const DashboardLayout = () => {
  const { isAuthenticated, logout, loading } = useAuth();
  const { t, language, toggleLanguage } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!loading && !isAuthenticated) {
      navigate('/login');
    }
  }, [isAuthenticated, loading, navigate]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-background">
        <div className="text-lg">{t('loading')}</div>
      </div>
    );
  }

  const navItems = [
    { path: '/', label: t('dashboard'), icon: LayoutDashboard },
    { path: '/zone-summary', label: language === 'en' ? 'Zone Summary' : 'झोन सारांश', icon: MapPinned },
    { path: '/leadership-selection', label: language === 'en' ? 'Leadership Selection' : 'नेतृत्व निवड', icon: Crown },
    { path: '/report', label: language === 'en' ? 'Report & Download' : 'अहवाल आणि डाउनलोड', icon: FileText },
    { path: '/excel-upload', label: language === 'en' ? 'Excel Upload' : 'Excel अपलोड', icon: Upload },
    { path: '/polling-percentage', label: t('pollingPercentage'), icon: Vote },
    { path: '/elected-candidates', label: t('electedCandidates'), icon: Users },
    { path: '/partywise-voting', label: t('partywiseVoting'), icon: FileBarChart },
    { path: '/seats-won', label: t('seatsWon'), icon: Landmark },
    { path: '/materials-used', label: t('materialsUsed'), icon: Package },
    { path: '/population-reservation', label: t('populationReservation'), icon: Map },
  ];

  return (
    <div className="flex h-screen bg-background">
      {/* Sidebar */}
      <aside className="w-64 bg-card border-r border-border flex flex-col">
        <div className="p-6 border-b border-border">
          <div className="flex items-center gap-3">
            <Landmark className="h-8 w-8 text-primary" />
            <div>
              <h1 className={`text-lg font-bold text-primary ${language === 'mr' ? 'marathi-text' : ''}`}>
                {language === 'en' ? 'NMC' : 'NMC'}
              </h1>
              <p className={`text-xs text-muted-foreground ${language === 'mr' ? 'marathi-text' : ''}`}>
                {language === 'en' ? 'Election 2025-26' : 'निवडणूक 2025-26'}
              </p>
            </div>
          </div>
        </div>

        <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                data-testid={`nav-${item.path.replace('/', '') || 'dashboard'}`}
              >
                <Button
                  variant={isActive ? 'default' : 'ghost'}
                  className={`w-full justify-start gap-3 ${language === 'mr' ? 'marathi-text' : ''}`}
                >
                  <Icon className="h-4 w-4" />
                  <span className="text-sm">{item.label}</span>
                </Button>
              </Link>
            );
          })}
        </nav>

        <div className="p-4 border-t border-border space-y-2">
          <Button
            variant="outline"
            className={`w-full justify-start gap-3 ${language === 'mr' ? 'marathi-text' : ''}`}
            onClick={toggleLanguage}
            data-testid="language-toggle-btn"
          >
            <Languages className="h-4 w-4" />
            <span className="text-sm">{language === 'en' ? 'मराठी' : 'English'}</span>
          </Button>
          <Button
            variant="destructive"
            className={`w-full justify-start gap-3 ${language === 'mr' ? 'marathi-text' : ''}`}
            onClick={logout}
            data-testid="logout-btn"
          >
            <LogOut className="h-4 w-4" />
            <span className="text-sm">{t('logout')}</span>
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-y-auto">
        <div className="max-w-7xl mx-auto p-6 md:p-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default DashboardLayout;